import { supabase } from '@/lib/supabase';

/* ------------------------------------------------------------------ */
/* Deduction categories (database-driven, replaces the old fixed enum) */
/* ------------------------------------------------------------------ */
export interface DeductionCategory {
  id: string;
  tenant_id: string | null;
  category_key: string;
  label: string;
  affects_net: boolean;
  is_insurance: boolean;
  sort_order: number;
  is_active: boolean;
  /** How this category's deductions get paid out in the banking file:
   *  'none' -- reduces net pay only, no separate line;
   *  'company_wide' -- every driver's deduction pays the SAME shared account
   *    below (e.g. the Insurance Excess Fund);
   *  'individual' -- each driver's own deduction row carries its own payee
   *    bank account (e.g. a court fine payable to a different account per
   *    driver), entered on their Deductions tab instead of here. */
  payout_mode: 'none' | 'company_wide' | 'individual';
  /** Only used when payout_mode = 'company_wide'. */
  bank_account_number: string | null;
  bank_account_name: string | null;
}

/* Fallback used only if the DB has no rows yet (first run / offline). */
export const DEFAULT_DEDUCTION_CATEGORIES: Array<Pick<DeductionCategory, 'category_key' | 'label' | 'is_insurance' | 'sort_order'>> = [
  { category_key: 'insurance', label: 'Insurance', is_insurance: true, sort_order: 10 },
  { category_key: 'structured_repayment', label: 'Structured Repayment', is_insurance: false, sort_order: 20 },
  { category_key: 'manual', label: 'Manual', is_insurance: false, sort_order: 30 },
  { category_key: 'insurance_rebate', label: 'Insurance Rebate', is_insurance: false, sort_order: 40 },
];

export const loadDeductionCategories = async (
  tenantId?: string | null,
): Promise<DeductionCategory[]> => {
  let query = supabase
    .from('deduction_categories')
    .select('id, tenant_id, category_key, label, affects_net, is_insurance, sort_order, is_active, payout_mode, bank_account_number, bank_account_name')
    .eq('is_active', true)
    .order('sort_order', { ascending: true });

  if (tenantId) query = query.eq('tenant_id', tenantId);

  const { data, error } = await query;
  if (error) {
    console.error('Error loading deduction categories:', error);
    return [];
  }
  return (data as DeductionCategory[]) || [];
};

/* ------------------------------------------------------------------ */
/* Payment categories -- reasons for an "extra payment" (Credit) to a    */
/* driver: sim card reimbursement, correcting an overpaid deduction, a   */
/* bonus, etc. Kept as its own list, separate from deduction categories, */
/* since it's a different concept (money TO the driver's own pay, not a  */
/* deduction routed to a third party) -- see driver_financial_adjustments*/
/* adjustment_type = 'credit'.                                           */
/* ------------------------------------------------------------------ */
export interface PaymentCategory {
  id: string;
  tenant_id: string | null;
  category_key: string;
  label: string;
  sort_order: number;
  is_active: boolean;
}

export const loadPaymentCategories = async (
  tenantId?: string | null,
): Promise<PaymentCategory[]> => {
  let query = supabase
    .from('payment_categories')
    .select('id, tenant_id, category_key, label, sort_order, is_active')
    .eq('is_active', true)
    .order('sort_order', { ascending: true });

  if (tenantId) query = query.eq('tenant_id', tenantId);

  const { data, error } = await query;
  if (error) {
    console.error('Error loading payment categories:', error);
    return [];
  }
  return (data as PaymentCategory[]) || [];
};

/* ------------------------------------------------------------------ */
/* Payout column -> payment-type mapping (stored in the settings table) */
/* ------------------------------------------------------------------ */

/* The turnover columns that exist on a weekly-payout job row. */
export const PAYOUT_COLUMNS: Array<{ key: string; label: string }> = [
  { key: 'cash_amt', label: 'Cash' },
  { key: 'eftpos_amt', label: 'EFTPOS' },
  { key: 'totalmobility_amt', label: 'Total Mobility' },
  { key: 'tm_hoist_amt', label: 'TM Hoist' },
  { key: 'account_amt', label: 'Account' },
  { key: 'ecard_amt', label: 'eCard' },
  { key: 'discount', label: 'Discount' },
  { key: 'other_amt', label: 'Other' },
];

/* Fallback mapping (column -> payment type name). null = no specific type
   (falls back to the flat company commission %). */
export const DEFAULT_PAYOUT_COLUMN_MAP: Record<string, string | null> = {
  cash_amt: 'Cash',
  eftpos_amt: 'EFTPOS',
  totalmobility_amt: 'TM Subsidy',
  tm_hoist_amt: 'TM Hoist',
  account_amt: 'Account',
  ecard_amt: 'e-card',
  discount: 'Discount',
  other_amt: 'Other',
};

const SETTING_PREFIX = 'payout_column_map.';

export const loadPayoutColumnMap = async (
  tenantId?: string | null,
): Promise<Record<string, string | null>> => {
  let query = supabase
    .from('settings')
    .select('name, value, tenant_id')
    .eq('category', 'PayoutMapping');

  if (tenantId) query = query.eq('tenant_id', tenantId);

  const { data, error } = await query;
  if (error) {
    console.error('Error loading payout column mapping:', error);
    return { ...DEFAULT_PAYOUT_COLUMN_MAP };
  }

  // Start from defaults so any missing column still resolves.
  const map: Record<string, string | null> = { ...DEFAULT_PAYOUT_COLUMN_MAP };
  (data || []).forEach((row: any) => {
    if (typeof row.name === 'string' && row.name.startsWith(SETTING_PREFIX)) {
      const col = row.name.slice(SETTING_PREFIX.length);
      const val = (row.value ?? '').toString().trim();
      // An empty saved value keeps the sensible default mapping (so newly added
      // core types like "Other" still resolve to their own commission %) instead
      // of silently dropping back to the flat company rate.
      map[col] = val === '' ? (DEFAULT_PAYOUT_COLUMN_MAP[col] ?? null) : val;
    }
  });
  return map;
};

/* Upsert one column mapping into the settings table. */
export const savePayoutColumnMap = async (
  map: Record<string, string | null>,
  tenantId: string | null,
): Promise<void> => {
  const rows = PAYOUT_COLUMNS.map((c, i) => ({
    name: `${SETTING_PREFIX}${c.key}`,
    value: map[c.key] ?? '',
    category: 'PayoutMapping',
    label: c.label,
    description: 'Maps a weekly-payout turnover column to a payment type name for commission calc.',
    type: 'text',
    sort_order: (i + 1) * 10,
    is_active: true,
    tenant_id: tenantId,
  }));

  // Delete existing rows for this tenant then re-insert (simple + consistent).
  let del = supabase.from('settings').delete().eq('category', 'PayoutMapping');
  if (tenantId) del = del.eq('tenant_id', tenantId);
  await del;

  const { error } = await supabase.from('settings').insert(rows);
  if (error) throw error;
};
